import { CampaignDetails } from "../types/CampaignAdminTypes"

/**
 * Sorts an array of CampaignDetails objects by their `from` date in descending order (newest first),
 * and uses `campaignId` as a tie-breaker when the dates are equal.
 * If the campaignId is not numeric, it falls back to comparing the `campaignId` field as strings.
 *
 * Example: The newer `from` date 2026-03-03 will be sorted before the older 2025-01-01.
 *
 * @param {CampaignDetails[]} campaignDetails - The array of campaign details to be sorted.
 * @returns {CampaignDetails[]} The sorted array.
 *
 * @throws {Error} If any `from` date in the array cannot be parsed into a valid JavaScript Date.
 */
export const sortCampaignDetailsByFromDateAndCampaignId = (
    campaignDetails: CampaignDetails[],
): CampaignDetails[] => {
    return campaignDetails.sort((a, b) => {
        const dateA = Date.parse(a.from)

        if (isNaN(dateA)) {
            throw new TypeError(
                `Invalid 'from' date in dateA campaignId=${a.campaignId}, from="${a.from}". Expected a valid ISO date string.`,
            )
        }

        const dateB = Date.parse(b.from)

        if (isNaN(dateB)) {
            throw new TypeError(
                `Invalid 'from' date in dateB campaignId=${b.campaignId}, from="${b.from}". Expected a valid ISO date string.`,
            )
        }

        // Primary sort: newest 'from' date first.
        const dateComparison = dateB - dateA

        // Exit early if sorting can be solved by date alone.
        if (dateComparison !== 0) {
            return dateComparison
        }

        // Secondary sort: Use campaignId as Tie-Breaker.
        const idA = a.campaignId
        const idB = b.campaignId

        // If both campaignId's are numeric sort higest numeric value first.
        if (!isNaN(idA) && !isNaN(idB)) {
            return idB - idA
        }

        // Fallback to string comparison if campaignId's are not numeric.
        return b.id.localeCompare(a.id)
    })
}
